import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Wine, History, Plus, LogOut, Menu, X, GitCompareArrows, CloudOff, RefreshCw, GraduationCap, Users, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { flushQueue, pendingCount } from "@/lib/offlineQueue";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/vinos", label: "Mis Vinos", icon: Wine },
  { to: "/app/historial", label: "Historial de Catas", icon: History },
  { to: "/app/comparar", label: "Comparar", icon: GitCompareArrows },
  { to: "/app/comunidad", label: "Catas Compartidas", icon: Users },
  { to: "/aprende", label: "Aprende a Catar", icon: GraduationCap },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const refreshCount = async () => setPending(await pendingCount());
    refreshCount();

    const syncNow = async () => {
      const n = await flushQueue();
      await refreshCount();
      if (n > 0) {
        toast.success(`${n} cata(s) sincronizada(s) correctamente.`);
        qc.invalidateQueries({ queryKey: ["tastings"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        qc.invalidateQueries({ queryKey: ["usage"] });
      }
    };

    const onOnline = () => { setOffline(false); syncNow(); };
    const onOffline = () => setOffline(true);
    const onQueueChange = (e) => setPending(e.detail?.count ?? 0);
    const onSwMessage = (e) => {
      if (e.data?.type === "SYNCED") {
        refreshCount();
        toast.success(`${e.data.count} cata(s) sincronizada(s) correctamente.`);
        qc.invalidateQueries({ queryKey: ["tastings"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        qc.invalidateQueries({ queryKey: ["usage"] });
      }
    };

    syncNow(); // sincroniza al abrir la app
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queue-changed", onQueueChange);
    if ("serviceWorker" in navigator) navigator.serviceWorker.addEventListener("message", onSwMessage);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queue-changed", onQueueChange);
      if ("serviceWorker" in navigator) navigator.serviceWorker.removeEventListener("message", onSwMessage);
    };
  }, [qc]);

  const NavLinks = () => {
    const items = user?.is_admin ? [...NAV, { to: "/app/admin", label: "Administración", icon: ShieldCheck }] : NAV;
    return (
    <nav className="flex flex-col gap-1 mt-8">
      {items.map(({ to, label, icon: Icon }) => {
        const active = location.pathname === to;
        return (
          <Link
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-300 ${
              active ? "bg-borgona-700 text-blanco" : "text-grises-600 hover:bg-borgona-50 hover:text-borgona-700"
            }`}
          >
            <Icon className="w-5 h-5" strokeWidth={1.75} />
            {label}
          </Link>
        );
      })}
    </nav>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full p-6">
      <Link to="/app" className="flex items-center gap-3" onClick={() => setOpen(false)}>
        <img src="/vinote-logo.png" alt="Vinonotas" className="w-11 h-11 object-contain rounded-lg" />
        <div>
          <div className="font-serif text-xl tracking-tight text-borgona-900 leading-none">Vinonotas</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-dorado-700 mt-1">Cata · Registra · Descubre</div>
        </div>
      </Link>
      <NavLinks />
      <div className="mt-auto">
        <Button
          data-testid="new-tasting-sidebar-btn"
          onClick={() => { navigate("/app/catas/nueva"); setOpen(false); }}
          className="w-full bg-dorado-500 text-negro hover:bg-dorado-700 rounded-full font-medium mb-4 transition-colors duration-300"
        >
          <Plus className="w-4 h-4 mr-1" /> Nueva Cata
        </Button>
        <div className="border-t border-grises-200 pt-4">
          <div className="text-sm font-medium text-negro truncate">{user?.name}</div>
          <div className="text-xs text-grises-500 truncate mb-3">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex items-center gap-2 text-sm text-grises-600 hover:text-borgona-700 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-blanco-off flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-72 bg-blanco border-r border-grises-200 fixed h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-16 bg-blanco/80 backdrop-blur-xl border-b border-grises-200 flex items-center justify-between px-4">
        <Link to="/app" className="flex items-center gap-2">
          <img src="/vinote-logo.png" alt="Vinonotas" className="w-9 h-9 object-contain rounded-md" />
          <span className="font-serif text-lg text-borgona-900">Vinonotas</span>
        </Link>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)}>
          <Menu className="w-6 h-6 text-borgona-700" />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-negro/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-blanco shadow-2xl">
            <button className="absolute top-5 right-5 z-10" onClick={() => setOpen(false)}>
              <X className="w-6 h-6 text-grises-500" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0 flex flex-col">
        {(offline || pending > 0) && (
          <div data-testid="offline-banner" className={`px-5 sm:px-8 py-2.5 text-sm flex items-center gap-2 ${offline ? "bg-borgona-700 text-blanco" : "bg-dorado-500 text-negro"}`}>
            {offline ? <CloudOff className="w-4 h-4 shrink-0" /> : <RefreshCw className="w-4 h-4 shrink-0" />}
            <span>
              {offline
                ? `Sin conexión. Tus catas se guardan en el dispositivo${pending > 0 ? ` (${pending} pendiente${pending > 1 ? "s" : ""})` : ""}.`
                : `Sincronizando ${pending} cata${pending > 1 ? "s" : ""} pendiente${pending > 1 ? "s" : ""}...`}
            </span>
          </div>
        )}
        <div className="max-w-6xl mx-auto w-full px-5 sm:px-8 py-8 flex-1">{children}</div>
        <footer className="border-t border-grises-200 bg-blanco/60">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-grises-500">
            <span data-testid="app-footer-moderacion">© {new Date().getFullYear()} Vinonotas · Bebe con moderación 🍷</span>
            <div className="flex items-center gap-5">
              <Link to="/terminos" data-testid="footer-terminos-link" className="hover:text-borgona-700 transition-colors">Términos</Link>
              <Link to="/privacidad" data-testid="footer-privacidad-link" className="hover:text-borgona-700 transition-colors">Política de Privacidad</Link>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
